import {Box3, Euler, Vector3} from "three";
import {Text} from "../baseElements/text";
import {buildingGroup, measurementsGroup} from "../../main";
import {getOuterWalls} from "../../utils/configuratorUtils";
import {findChildrenByName, setBox3FromObjectArray} from "../../utils/threeUtils";
import {sortBySubKey, toFixedFloat} from "../../utils/utils";
import {getWallOrientation} from "../walls/wall";
import {clearGroup} from "../../sceneManager";
import {supportBeamMeshName} from "../../constants/nameConstants";

export const measurementSides = {
    left: 1,
    right: 2,
    front: 3,
    back: 4
};

export const measurementsYOffset = 0.05;
export const scaleFactorDefaultBoundLimit = 10; //Ab dieser Gebäudegröße werden Bemaßungen skaliert, um lesbar zu bleiben #13053
export const defaultScaleFactor = 1; //Ab dieser Gebäudegröße werden Bemaßungen skaliert, um lesbar zu bleiben #13053
export const maxScaleFactor = 3; //Oberes Limit für Bemaßungsskalierung, um Überschneidungen möglichst zu vermeiden.

export class Measurements {
    constructor() {
        this.supportBeams = findChildrenByName(buildingGroup, supportBeamMeshName, false, true);
        this.outerWalls = getOuterWalls().concat(this.supportBeams);
        //Nur generieren, wenn mindestens 4 Außenwände vorhanden sind. Darunter nicht sinnvoll
        if (this.outerWalls.length < 4) {
            return null;
        }

        this.outerWallsBounds = setBox3FromObjectArray(this.outerWalls);
        this.walls = {
            left: [],
            right: [],
            front: [],
            back: []
        }; //Nach position aufgeteilte Wände

        this.setMeasurementsScaleFactor();
    }

    static createMeasurementText(value, position) {
        value = toFixedFloat(value);
        if (Number.isInteger(value)) {
            value = value.toFixed(2);
        }
        let text = value.toString();
        text = text.substring(0, text.length > 5 ? 5 : text.length);
        let rotation = new Euler(-Math.PI / 2, 0, 0);
        let textObj = new Text(text, 0.1, 'center', -0.18, position, rotation);
        textObj.name = "measurementValue";
        return textObj;
    }

    static clearMeasurements() {
        clearGroup(measurementsGroup);
    }

    setMeasurementsScaleFactor() {
        this.scaleFactor = defaultScaleFactor; //Default value

        //Skalierungsfaktor anpassen, wenn Gebäudemaße das Limit überschreiten
        let wallBoundsSize = this.outerWallsBounds.getSize(new Vector3());
        if (wallBoundsSize.x > scaleFactorDefaultBoundLimit || wallBoundsSize.z > scaleFactorDefaultBoundLimit) {
            let factorX = toFixedFloat(wallBoundsSize.x / scaleFactorDefaultBoundLimit, 1);
            let factorZ = toFixedFloat(wallBoundsSize.z / scaleFactorDefaultBoundLimit, 1);

            this.scaleFactor = factorX > factorZ ? factorX : factorZ;
        }

        //Limitierung des Skalierungsfaktor
        if (this.scaleFactor > maxScaleFactor) {
            this.scaleFactor = maxScaleFactor;
        }
    }

    sortWallsBySide() {
        let size = this.outerWallsBounds.getSize(new Vector3());

        let result = this.sortWallsByOrientation(this.outerWalls);

        let xOrientedWalls = result.x;
        let zOrientedWalls = result.z;

        //Zuweisung der nach Position sortieren Wände in Links/rechts und Vorne/Hinten
        let currentLeftSum = 0;
        for (let i = 0; i < xOrientedWalls.length; i++) {
            let currentXOrientedWall = xOrientedWalls[i];
            let currentWallSize = new Box3().setFromObject(currentXOrientedWall).getSize(new Vector3()).x;
            currentLeftSum += currentWallSize;
            if (toFixedFloat(currentLeftSum) <= toFixedFloat(size.x)) {
                this.walls.back.push(currentXOrientedWall);
            } else {
                this.walls.front.push(currentXOrientedWall);
            }
        }

        let currentTopSum = 0;
        for (let i = 0; i < zOrientedWalls.length; i++) {
            let currentZOrientedWall = zOrientedWalls[i];
            let currentWallSize = new Box3().setFromObject(currentZOrientedWall).getSize(new Vector3()).z;
            currentTopSum += currentWallSize;
            if (toFixedFloat(currentTopSum) <= toFixedFloat(size.z)) {
                this.walls.left.push(currentZOrientedWall);
            } else {
                this.walls.right.push(currentZOrientedWall);
            }
        }

        this.walls.front.reverse();
        this.walls.right.reverse();
    }

    sortWallsByOrientation(outerWalls) {
        let xOrientedWalls = [];
        let zOrientedWalls = [];

        for (let i = outerWalls.length - 1; i >= 0; i--) {
            let wallOrientation = getWallOrientation(outerWalls[i]);
            if (wallOrientation === "x") {
                xOrientedWalls.push(outerWalls[i]);
            } else {
                zOrientedWalls.push(outerWalls[i]);
            }
        }

        xOrientedWalls = sortBySubKey(xOrientedWalls, "position", "z");
        zOrientedWalls = sortBySubKey(zOrientedWalls, "position", "x");

        return {x: xOrientedWalls, z: zOrientedWalls};
    }
}
